const DATABASE_VERSION = 1;
const ATTACHMENTS_STORE = "attachments";
const COMPOSER_FILE_ATTACHMENT_DATABASE = "forge-composer-file-attachments";

export interface ComposerFileAttachmentRef {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
}

interface StoredComposerFileAttachment extends ComposerFileAttachmentRef {
  readonly bytes: Blob;
  readonly createdAt?: number;
}

export interface StoredComposerFileAttachmentRef extends ComposerFileAttachmentRef {
  readonly createdAt: number;
}

export type BrowserFileAttachmentStoreErrorCode = "unavailable" | "quota" | "corrupt" | "missing";

export class BrowserFileAttachmentStoreError extends Error {
  constructor(
    readonly code: BrowserFileAttachmentStoreErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "BrowserFileAttachmentStoreError";
  }
}

export interface BrowserFileAttachmentStore {
  /** Resolves only after IndexedDB commits the attachment bytes. */
  readonly put: (file: File) => Promise<ComposerFileAttachmentRef>;
  /** Rebuilds a File after checking stored metadata and byte length against the ref. */
  readonly read: (ref: ComposerFileAttachmentRef) => Promise<File>;
  /** Call only after the draft and every stash that references this ref releases it. */
  readonly delete: (id: string) => Promise<void>;
  /** Lists verified metadata for orphan collection. It never returns attachment bytes. */
  readonly list: () => Promise<ReadonlyArray<StoredComposerFileAttachmentRef>>;
  /** Releases the IndexedDB connection. Later calls reopen it. */
  readonly close: () => void;
}

export interface CreateBrowserFileAttachmentStoreOptions {
  readonly databaseName?: string;
  readonly indexedDB?: IDBFactory | null;
  readonly createId?: () => string;
}

function unavailable(message: string): BrowserFileAttachmentStoreError {
  return new BrowserFileAttachmentStoreError("unavailable", message);
}

function storageError(error: unknown): BrowserFileAttachmentStoreError {
  if (error instanceof BrowserFileAttachmentStoreError) return error;
  if (error instanceof DOMException && error.name === "QuotaExceededError") {
    return new BrowserFileAttachmentStoreError("quota", "Browser storage is full.");
  }
  return unavailable("Browser file storage is unavailable.");
}

function resolveIndexedDB(factory: IDBFactory | null | undefined): IDBFactory | null {
  if (factory !== undefined) return factory;
  return typeof indexedDB === "undefined" ? null : indexedDB;
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener("error", () => reject(transaction.error), { once: true });
    transaction.addEventListener("abort", () => reject(transaction.error), { once: true });
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener("error", () => reject(request.error), { once: true });
  });
}

async function completeRequest<T>(
  transaction: IDBTransaction,
  request: () => IDBRequest<T>,
): Promise<T> {
  const completed = transactionComplete(transaction);
  try {
    const [result] = await Promise.all([requestResult(request()), completed]);
    return result;
  } catch (error) {
    await completed.catch(() => undefined);
    throw error;
  }
}

function hasValidMetadata(value: unknown): value is ComposerFileAttachmentRef {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    candidate.id.length > 0 &&
    typeof candidate.name === "string" &&
    typeof candidate.mimeType === "string" &&
    typeof candidate.sizeBytes === "number" &&
    Number.isSafeInteger(candidate.sizeBytes) &&
    candidate.sizeBytes >= 0
  );
}

function matchesRef(record: ComposerFileAttachmentRef, ref: ComposerFileAttachmentRef): boolean {
  return (
    record.id === ref.id &&
    record.name === ref.name &&
    record.mimeType === ref.mimeType &&
    record.sizeBytes === ref.sizeBytes
  );
}

function isStoredAttachment(value: unknown): value is StoredComposerFileAttachment {
  if (!hasValidMetadata(value) || typeof Blob === "undefined" || !("bytes" in value)) return false;
  return (value as StoredComposerFileAttachment).bytes instanceof Blob;
}

function fileFromRecord(
  record: StoredComposerFileAttachment,
  ref: ComposerFileAttachmentRef,
): File {
  if (
    !matchesRef(record, ref) ||
    record.bytes.size !== ref.sizeBytes ||
    record.bytes.type !== ref.mimeType
  ) {
    throw new BrowserFileAttachmentStoreError("corrupt", "Stored file metadata does not match.");
  }
  if (typeof File === "undefined") throw unavailable("This browser cannot rebuild selected files.");
  const file = new File([record.bytes], ref.name, { type: ref.mimeType });
  if (file.name !== ref.name || file.type !== ref.mimeType || file.size !== ref.sizeBytes) {
    throw new BrowserFileAttachmentStoreError("corrupt", "Stored file size could not be verified.");
  }
  return file;
}

/**
 * Durable browser-only bytes for deferred composer attachments. Draft and stash
 * persistence keep only the returned refs. There is intentionally no GC here.
 */
export function createBrowserFileAttachmentStore(
  options: CreateBrowserFileAttachmentStoreOptions = {},
): BrowserFileAttachmentStore {
  const factory = resolveIndexedDB(options.indexedDB);
  const databaseName = options.databaseName ?? COMPOSER_FILE_ATTACHMENT_DATABASE;
  const createId = options.createId ?? (() => crypto.randomUUID());
  let database: IDBDatabase | null = null;
  let opening: Promise<IDBDatabase> | null = null;

  const open = async (): Promise<IDBDatabase> => {
    if (database) return database;
    if (opening) return opening;
    if (!factory) throw unavailable("This browser does not provide IndexedDB.");

    let resolveOpening: (database: IDBDatabase) => void;
    let rejectOpening: (error: BrowserFileAttachmentStoreError) => void;
    const pending = new Promise<IDBDatabase>((resolve, reject) => {
      resolveOpening = resolve;
      rejectOpening = reject;
    });
    opening = pending;
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(databaseName, DATABASE_VERSION);
    } catch (error) {
      if (opening === pending) opening = null;
      rejectOpening!(storageError(error));
      return pending;
    }
    {
      let settled = false;
      const fail = (error: BrowserFileAttachmentStoreError) => {
        if (settled) return;
        settled = true;
        if (opening === pending) opening = null;
        rejectOpening!(error);
      };
      request.addEventListener("upgradeneeded", () => {
        try {
          if (!request.result.objectStoreNames.contains(ATTACHMENTS_STORE)) {
            request.result.createObjectStore(ATTACHMENTS_STORE, { keyPath: "id" });
          }
        } catch (error) {
          request.transaction?.abort();
          fail(storageError(error));
        }
      });
      request.addEventListener("error", () => fail(storageError(request.error)), { once: true });
      request.addEventListener(
        "blocked",
        () => fail(unavailable("Browser file storage is blocked by another tab.")),
        { once: true },
      );
      request.addEventListener("success", () => {
        const opened = request.result;
        if (settled) {
          opened.close();
          return;
        }
        settled = true;
        database = opened;
        if (opening === pending) opening = null;
        opened.addEventListener("versionchange", () => {
          opened.close();
          if (database === opened) database = null;
        });
        resolveOpening!(opened);
      });
    }
    return pending;
  };

  return {
    put: async (file) => {
      if (typeof File === "undefined" || !(file instanceof File)) {
        throw unavailable("A browser File is required for attachment storage.");
      }
      const ref: ComposerFileAttachmentRef = {
        id: createId(),
        name: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      };
      if (!hasValidMetadata(ref)) throw unavailable("Could not create an attachment reference.");
      try {
        const db = await open();
        const transaction = db.transaction(ATTACHMENTS_STORE, "readwrite");
        await completeRequest(transaction, () =>
          transaction.objectStore(ATTACHMENTS_STORE).add({
            ...ref,
            bytes: new Blob([file], { type: ref.mimeType }),
            createdAt: Date.now(),
          } satisfies StoredComposerFileAttachment),
        );
        return ref;
      } catch (error) {
        throw storageError(error);
      }
    },
    read: async (ref) => {
      if (!hasValidMetadata(ref)) {
        throw new BrowserFileAttachmentStoreError("corrupt", "Attachment metadata is invalid.");
      }
      try {
        const db = await open();
        const transaction = db.transaction(ATTACHMENTS_STORE, "readonly");
        const record = await completeRequest(transaction, () =>
          transaction.objectStore(ATTACHMENTS_STORE).get(ref.id),
        );
        if (record === undefined) {
          throw new BrowserFileAttachmentStoreError(
            "missing",
            "The selected file is no longer available.",
          );
        }
        if (!isStoredAttachment(record)) {
          throw new BrowserFileAttachmentStoreError("corrupt", "Stored file data is invalid.");
        }
        return fileFromRecord(record, ref);
      } catch (error) {
        throw storageError(error);
      }
    },
    delete: async (id) => {
      if (id.length === 0) return;
      try {
        const db = await open();
        const transaction = db.transaction(ATTACHMENTS_STORE, "readwrite");
        await completeRequest(transaction, () =>
          transaction.objectStore(ATTACHMENTS_STORE).delete(id),
        );
      } catch (error) {
        throw storageError(error);
      }
    },
    list: async () => {
      try {
        const db = await open();
        const transaction = db.transaction(ATTACHMENTS_STORE, "readonly");
        const records = await completeRequest(transaction, () =>
          transaction.objectStore(ATTACHMENTS_STORE).getAll(),
        );
        if (!Array.isArray(records)) {
          throw new BrowserFileAttachmentStoreError("corrupt", "Stored file data is invalid.");
        }
        return records.flatMap((record) => {
          if (!isStoredAttachment(record)) return [];
          const createdAt = record.createdAt;
          if (typeof createdAt !== "number" || !Number.isSafeInteger(createdAt)) return [];
          const { id, name, mimeType, sizeBytes } = record;
          return [{ id, name, mimeType, sizeBytes, createdAt }];
        });
      } catch (error) {
        throw storageError(error);
      }
    },
    close: () => {
      database?.close();
      database = null;
    },
  };
}

export const composerFileAttachmentStore = createBrowserFileAttachmentStore();
